import React, { useState, useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';
import { Toaster } from 'react-hot-toast';

// Layout
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import PromoRibbon from './components/PromoRibbon';
import LoadingScreen from './components/LoadingScreen';

// Pages
import HomePage from './components/HomePage';
import Profile from './components/Profile/Profile';
import Wishlist from './components/Wishlist/Wishlist';
import Cart from './components/Cart/Cart';
import OrderHistory from './components/Orders/OrderHistory';
import Settings from './components/Settings/Settings';
import SignUp from './components/Auth/SignUp';
import SignIn from './components/Auth/Signln';
import ForgotPassword from './components/Auth/ForgotPassword';
import ProductListing from './components/ProductDetails/ProductListing';
import Products from './components/Products/Products';
import FilteredProducts from './components/FilteredProducts/FilteredProducts';
import BlogPage from './components/Policies/Blog';
import BlogPost from './components/Policies/BlogPost';
import NotFound from './components/NotFound';

// Policies
import Terms from './components/Policies/Terms';
import Contactus from './components/Policies/Contactus';
import AboutUs from './components/Policies/AboutUs';
import Faqs from './components/Policies/Faqs';
import PrivacyPolicy from './components/Policies/PrivacyPolicy';
import PaymentPolicy from './components/Policies/PaymentPolicy';
import RefundReturnPolicy from './components/Policies/RefundReturnPolicy';
import Cookies from './components/Policies/Cookie.jsx';

// Context Providers
import { ReviewProvider } from './components/context/ReviewContext';
import { CategoriesProvider } from './components/context/CategoriesContext';
import { ProductProvider } from './components/context/ProductContext';
import { FaqProvider } from './components/context/FaqContext';
import { AuthProvider } from './components/context/AuthContext';
import { ProfileProvider } from './components/context/ProfileContext';
import { CartProvider } from './components/context/CartContext';
import Report from './components/Settings/Report.jsx';

// Protected Route
import ProtectedRoute from './components/config/ProtectedRoute';

/* =========================
   Page Transition Wrapper
========================= */
const PageTransition = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.3, ease: 'easeInOut' }}
  >
    {children}
  </motion.div>
);

/* =========================
   Animated Routes
========================= */
const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>

        {/* PUBLIC ROUTES */}
        <Route path="/" element={<PageTransition><HomePage /></PageTransition>} />
        <Route path="/signin" element={<PageTransition><SignIn /></PageTransition>} />
        <Route path="/signup" element={<PageTransition><SignUp /></PageTransition>} />
        <Route path="/forgot-password" element={<PageTransition><ForgotPassword /></PageTransition>} />

        <Route path="/products" element={<PageTransition><Products /></PageTransition>} />
        <Route path="/products/category/:categoryId" element={<PageTransition><FilteredProducts /></PageTransition>} />
        <Route path="/product/:id" element={<PageTransition><ProductListing /></PageTransition>} />

        <Route path="/about" element={<PageTransition><AboutUs /></PageTransition>} />
        <Route path="/terms&conditions" element={<PageTransition><Terms /></PageTransition>} />
        <Route path="/contact" element={<PageTransition><Contactus /></PageTransition>} />
        <Route path="/faq" element={<PageTransition><Faqs /></PageTransition>} />
        <Route path="/blog" element={<PageTransition><BlogPage /></PageTransition>} />
        <Route path="/blog/:slug" element={<PageTransition><BlogPost /></PageTransition>} />

        <Route path="/cookie-policy" element={<Cookies />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/payment-policy" element={<PaymentPolicy />} />
        <Route path="/refund-policy" element={<RefundReturnPolicy />} />

        {/* PROTECTED ROUTES */}
        <Route element={<ProtectedRoute />}>
          <Route path="/profile" element={<PageTransition><Profile /></PageTransition>} />
          <Route path="/wishlist" element={<PageTransition><Wishlist /></PageTransition>} />
          <Route path="/cart" element={<PageTransition><Cart /></PageTransition>} />
          <Route path="/orders" element={<PageTransition><OrderHistory /></PageTransition>} />
          <Route path="/settings" element={<PageTransition><Settings /></PageTransition>} />
          <Route path="/report" element={<PageTransition><Report /></PageTransition>} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />

      </Routes>
    </AnimatePresence>
  );
};

/* =========================
   App Root
========================= */
function App() {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const navEntries = performance.getEntriesByType("navigation");
    const isReload =
      navEntries.length > 0 && navEntries[0].type === "reload";

    if (isReload) {
      setIsLoading(true);
    }
  }, []);

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  if (isLoading) {
    return <LoadingScreen onLoadingComplete={handleLoadingComplete} />;
  }

  return (
    <BrowserRouter basename="/">
      <AuthProvider>
        <CartProvider>
          <ScrollToTop />
          <ReviewProvider>
            <CategoriesProvider>
              <ProductProvider>
                <FaqProvider>
                  <ProfileProvider>

                    <div className="App bg-orange-50 bg-opacity-90 min-h-screen flex flex-col">
                      <PromoRibbon />
                      <Header />

                      <main className="pt-0 flex-grow">
                        <AnimatedRoutes />
                      </main>

                      <Footer />

                      <Toaster
                        position="top-right"
                        containerStyle={{
                          top: '120px',
                          right: '16px',
                          zIndex: 9999,
                        }}
                      />

                    </div>

                  </ProfileProvider>
                </FaqProvider>
              </ProductProvider>
            </CategoriesProvider>
          </ReviewProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
